// Универсальные утилиты для ленивого постраничного кэша списков
// Хранение единого массива ids по скоупам + нормализованный словарь byId

export type PagedId = string | number;
export type ScopeKey = string;

export interface PagedScopeState<T> {
  ids: PagedId[];
  nextOffset: number; // следующий offset для фетча
  pageSize: number; // последний применённый limit
  hasMore: boolean; // можно ли ещё грузить
  receivedAt: number; // время последнего обновления
}

export interface PagedDictionary<T> {
  byId: Record<PagedId, T>;
  scopes: Record<ScopeKey, PagedScopeState<T>>;
}

// Стабильный генератор ключей скоупа (например: entity: 'championships', tournamentId, фильтры)
export function makeScopeKey(parts: Record<string, any>): ScopeKey {
  return Object.entries(parts)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}:${JSON.stringify(v)}`)
    .join('|');
}

// Добавить пачку элементов в скоуп
export function appendBatch<T extends { id: PagedId }>(state: PagedDictionary<T>, scope: ScopeKey, batch: T[], limit: number) {
  const scopeState: PagedScopeState<T> = state.scopes[scope] ?? {
    ids: [],
    nextOffset: 0,
    pageSize: limit,
    hasMore: true,
    receivedAt: 0,
  };

  // нормализация
  for (const item of batch) state.byId[item.id] = item as any;

  // мердж ids без дублей (сохранение порядка поступления)
  const existing = new Set(scopeState.ids);
  for (const item of batch) if (!existing.has(item.id)) scopeState.ids.push(item.id);

  // пересчёты
  scopeState.pageSize = limit;
  scopeState.nextOffset = scopeState.ids.length; // для offset-based API
  scopeState.hasMore = batch.length >= limit; // меньше лимита — данных больше нет
  scopeState.receivedAt = Date.now();

  state.scopes[scope] = scopeState;
}

// Вырезать слайс для страницы из накопленного массива
export function selectSlice<T>(
  state: PagedDictionary<T>,
  scope: ScopeKey,
  page: number,
  perPage: number,
): { items: T[]; totalKnown: number } {
  const s = state.scopes[scope];
  if (!s) return { items: [], totalKnown: 0 };
  const start = Math.max(0, (page - 1) * perPage);
  const end = start + perPage;
  const ids = s.ids.slice(start, end);
  const items = ids.map((id) => state.byId[id]).filter(Boolean) as T[];
  return { items, totalKnown: s.ids.length };
}

// Нужно ли догрузить ещё, чтобы покрыть запрошенную страницу
export function shouldFetchMore(state: PagedDictionary<any>, scope: ScopeKey, page: number, perPage: number): boolean {
  const s = state.scopes[scope];
  if (!s) return true; // скоуп пуст — точно нужно грузить
  const needed = page * perPage; // сколько элементов нужно иметь, чтобы отдать page
  const have = s.ids.length;
  return s.hasMore && have < needed;
}
